<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <v-row align="end" justify="space-between" class="mb-1">
      <v-col cols="auto">
        <div v-if="groceries.length > 0">
          <v-chip small class="mr-2" color="deep-purple" text-color="white">
            <v-icon small left>mdi-clipboard-check</v-icon>
            {{ $t("counted") }}: {{ countedCount }}/{{ groceries.length }}
          </v-chip>
          <v-chip small class="mr-2" color="grey" text-color="white">
            <v-icon small left>mdi-package-variant</v-icon>
            {{ $t("total") }}: {{ groceries.length }}
          </v-chip>
          <v-tooltip bottom v-if="changesCount.additions > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                class="mr-2"
                color="success"
                text-color="white"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small left>mdi-arrow-up-bold</v-icon>
                {{ changesCount.additions }}
              </v-chip>
            </template>
            <span>{{ $t("items_with_surplus") }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="changesCount.reductions > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-chip small color="error" text-color="white" v-bind="attrs" v-on="on">
                <v-icon small left>mdi-arrow-down-bold</v-icon>
                {{ changesCount.reductions }}
              </v-chip>
            </template>
            <span>{{ $t("items_with_shortage") }}</span>
          </v-tooltip>
        </div>
      </v-col>
      <v-col>
        <v-row no-gutters align="center" justify="end">
          <v-btn-toggle v-model="filterStatus" mandatory class="mr-4">
            <v-btn small value="all">
              {{ $t("all") }}
            </v-btn>
            <v-btn small value="counted">
              <v-icon small left color="success">mdi-check-circle</v-icon>
              {{ $t("counted") }}
            </v-btn>
            <v-btn small value="not_counted">
              <v-icon small left color="grey">mdi-checkbox-blank-circle-outline</v-icon>
              {{ $t("not_counted") }}
            </v-btn>
          </v-btn-toggle>
          <text-field
            v-model="search"
            :placeholder="$t('search')"
            hide-details
            color="white"
            kind="search"
          />
        </v-row>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredGroceries"
      item-key="id"
      class="striped-table ordera-box-shadow initial-state-table"
      :loading="loading"
      :items-per-page="10"
    >
      <template v-slot:item="{ item, index }">
        <tr
          :class="{
            'row-changed': hasChange(item),
            'row-new': isNewItem(item),
            'row-counted': isCounted(item),
          }"
        >
          <td class="text-center">
            <v-icon v-if="isCounted(item)" color="success" small>mdi-check-circle</v-icon>
            <v-icon v-else color="grey lighten-1" small>
              mdi-checkbox-blank-circle-outline
            </v-icon>
          </td>
          <td>{{ index + 1 }}</td>
          <td>{{ item.code }}</td>
          <td>{{ toLatin(item.name) }}</td>
          <td class="text-center">
            <span v-if="item.inWarehouse !== false" class="font-weight-bold">
              {{ item.current_quantity || 0 }}
              <span class="caption">{{ toLatin(item.measure) }}</span>
            </span>
            <v-chip v-else small color="grey lighten-1" text-color="grey darken-2">
              <v-icon small left>mdi-package-variant-remove</v-icon>
              {{ $t("not_in_stock") }}
            </v-chip>
          </td>
          <td class="text-center">
            <span v-if="isCompleted" class="font-weight-bold">
              {{ item.new_quantity || 0 }}
              <span class="caption">{{ toLatin(item.measure) }}</span>
            </span>
            <text-field
              v-else
              v-model.number="item.new_quantity"
              type="number"
              step="0.01"
              min="0"
              color="white"
              hide-details
              :suffix="toLatin(item.measure)"
              @input="updateQuantity(item)"
              @focus="onFieldFocus(item)"
              @blur="onFieldBlur(item)"
            />
          </td>
          <td class="text-center">
            <v-chip
              v-if="!isQuantityEmpty(item)"
              small
              :color="getDifferenceColor(item)"
              text-color="white"
            >
              {{ calculateDifference(item) > 0 ? "+" : ""
              }}{{ calculateDifference(item) }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-btn
              v-if="!isCompleted && canSave(item)"
              small
              color="success"
              depressed
              @click="saveItem(item)"
              :loading="item.saving"
            >
              <v-icon small left>mdi-content-save</v-icon>
              {{ $t("save") }}
            </v-btn>
          </td>
          <td>
            <remove-button v-if="!isCompleted" @click="removeGrocery(item)" />
          </td>
        </tr>
      </template>
      <template v-if="!isCompleted" v-slot:body.append>
        <tr class="add-grocery-row" @click="$emit('addGrocery')">
          <td :colspan="headers.length" class="text-center pa-4">
            <span class="add-grocery-content">
              <v-icon color="deep-purple" class="mr-2">mdi-plus-circle</v-icon>
              <span class="deep-purple--text font-weight-medium">
                {{ $t("add_new_grocery") }}
              </span>
            </span>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: "WarehouseListTable",
  emits: ["addGrocery", "removeGrocery", "saveItem"],
  props: {
    groceries: {
      type: Array,
      required: true,
    },
    warehouse: {
      type: [Object, null],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isCompleted: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: "",
    filterStatus: "all",
  }),
  watch: {
    isCompleted: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.filterStatus = "counted";
        }
      },
    },
  },
  methods: {
    isQuantityEmpty(item) {
      return (
        item.new_quantity === null ||
        item.new_quantity === undefined ||
        item.new_quantity === ""
      );
    },
    canSave(item) {
      return (
        (item.focused || item.saving) &&
        !this.isQuantityEmpty(item) &&
        item.new_quantity >= 0
      );
    },
    removeGrocery(item) {
      this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_grocery"),
          this.$t("warehouse.confirm_delete_grocery"),
          { color: "warning" }
        )
        .then(() => {
          this.$emit("removeGrocery", item);
        })
        .catch((err) => {
          // User cancelled - not an error
          if (err) console.error(err);
        });
    },
    updateQuantity(item) {
      if (this.isQuantityEmpty(item)) {
        item.new_quantity = 0;
      }
      const currentQty = parseFloat(item.current_quantity) || 0;
      const newQty = parseFloat(item.new_quantity) || 0;
      item.quantity = parseFloat((newQty - currentQty).toFixed(2));
    },
    isCounted(item) {
      return item.counted === true;
    },
    calculateDifference(item) {
      if (item.inWarehouse === false && this.isQuantityEmpty(item)) {
        return 0;
      }
      const currentQty = parseFloat(item.current_quantity) || 0;
      const newQty = parseFloat(item.new_quantity) || 0;
      return parseFloat((newQty - currentQty).toFixed(2));
    },
    getDifferenceColor(item) {
      const diff = this.calculateDifference(item);
      if (diff > 0) return "success";
      if (diff < 0) return "error";
      return "grey";
    },
    hasChange(item) {
      if (item.inWarehouse === false && this.isQuantityEmpty(item)) {
        return false;
      }
      return this.calculateDifference(item) !== 0;
    },
    isNewItem(item) {
      return parseFloat(item.current_quantity) === 0 && parseFloat(item.new_quantity) > 0;
    },
    onFieldFocus(item) {
      this.$set(item, "focused", true);
    },
    onFieldBlur(item) {
      // Delay to allow click on save button before hiding it
      setTimeout(() => {
        this.$set(item, "focused", false);
      }, 200);
    },
    saveItem(item) {
      this.$emit("saveItem", item);
    },
  },
  computed: {
    countedCount() {
      return this.groceries.filter((item) => this.isCounted(item)).length;
    },
    changesCount() {
      const entered = this.groceries.filter((item) => !this.isQuantityEmpty(item));
      const additions = entered.filter(
        (item) => this.calculateDifference(item) > 0
      ).length;
      const reductions = entered.filter(
        (item) => this.calculateDifference(item) < 0
      ).length;
      return { additions, reductions };
    },
    filteredGroceries() {
      let filtered = this.groceries;

      // Filter by counted status
      if (this.filterStatus === "counted") {
        filtered = filtered.filter((item) => this.isCounted(item));
      } else if (this.filterStatus === "not_counted") {
        filtered = filtered.filter((item) => !this.isCounted(item));
      }

      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.name?.toLowerCase().includes(searchLower) ||
            item.code?.toLowerCase().includes(searchLower)
        );
      }

      // Sort: counted first, then in warehouse, then not in stock at the end
      filtered = [...filtered].sort((a, b) => {
        // First priority: counted items first
        const aCounted = this.isCounted(a) ? 0 : 1;
        const bCounted = this.isCounted(b) ? 0 : 1;
        if (aCounted !== bCounted) return aCounted - bCounted;

        // Second priority: items in warehouse before items not in stock
        const aInWarehouse = a.inWarehouse !== false ? 0 : 1;
        const bInWarehouse = b.inWarehouse !== false ? 0 : 1;
        return aInWarehouse - bInWarehouse;
      });

      return filtered;
    },
    headers() {
      return [
        { text: "", value: "counted", width: "50px", sortable: false },
        { text: this.$t("r_b"), value: "num", width: "60px", sortable: false },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "100px",
        },
        { text: this.$t("title"), value: "name" },
        {
          text: this.$t("book_state"),
          value: "current_quantity",
          width: "150px",
          align: "center",
        },
        {
          text: this.$t("inventory_state"),
          value: "new_quantity",
          width: "150px",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("difference"),
          value: "difference",
          width: "120px",
          align: "center",
        },
        {
          text: "",
          value: "save_action",
          width: "120px",
          sortable: false,
          align: "center",
        },
        { text: "", value: "actions", width: "80px", sortable: false },
      ];
    },
  },
};
</script>

<style scoped>
.initial-state-table ::v-deep .row-changed {
  background-color: #fff3e0 !important;
}

.initial-state-table ::v-deep .row-new {
  background-color: #e8f5e9 !important;
}

.initial-state-table ::v-deep .row-counted {
  background-color: #f3e5f5 !important;
}

.initial-state-table ::v-deep .row-changed:hover,
.initial-state-table ::v-deep .row-new:hover {
  background-color: #ffe0b2 !important;
}

.initial-state-table ::v-deep .row-counted:hover {
  background-color: #e1bee7 !important;
}

.initial-state-table ::v-deep .add-grocery-row {
  cursor: pointer;
  background-color: rgba(103, 58, 183, 0.04) !important;
  transition: all 0.2s ease;
}

.initial-state-table ::v-deep .add-grocery-row td {
  border: 2px dashed rgba(103, 58, 183, 0.3) !important;
  border-left: none !important;
  border-right: none !important;
}

.initial-state-table ::v-deep .add-grocery-row .add-grocery-content {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.initial-state-table ::v-deep .add-grocery-row:hover {
  background-color: rgba(103, 58, 183, 0.08) !important;
}

.initial-state-table ::v-deep .add-grocery-row:hover td {
  border-color: rgba(103, 58, 183, 0.5) !important;
}

.initial-state-table ::v-deep .add-grocery-row:hover .add-grocery-content {
  opacity: 1;
}
</style>
